import * as React from 'react';
import { action, computed, observable, runInAction, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import _intersection from 'lodash/intersection';
import { WidgetDimensionKey } from '../../../utils/Widget/WidgetDimensionKey';
import MetaInfoService, {
    BulkLookupResponse,
} from '../../../services/MetaInfoService/MetaInfoService';
import { AnalyticsEngine } from '@sprinklr/stories/analytics/AnalyticsRequest';
import SprinklrAPIService, {
    BulkLookupValue,
    BulkLookupValues,
    ReportingLookupFilter,
} from '../../../services/SprinklrAPIService/SprinklrAPIService';
import { ChannelType } from '@sprinklr/stories/post/PostsRequest';
import { Picker, paginationCount } from './Picker';
import { removeDuplicateByKey } from '../../../utils/ArrayUtils/ArrayUtils';
import i18n from 'i18next';
import {
    LabeledCheckbox,
    Select,
    Flexbox,
    Channel,
    Avatar,
    TooltipPopper,
} from 'components/spaceKit';
import _difference from 'lodash/difference';
import { ColumnTypeFilter } from 'components/Panel/PanelDataPicker/types';
import StyledAsyncSelect from 'components/_UI/spaceweb/AsyncSelect/StyledAsyncSelect';
import {
    Value as AsyncSelectValue,
    Option as AsyncSelectOption,
} from '@sprinklr/spaceweb/asyncSelect';
import DimensionValues, { AsyncListItem } from 'models/MetaInfo/DimensionValues';
import _isEmpty from 'lodash/isEmpty';
import { Option } from 'baseui/select';
import { DEFAULT_AVATAR_URL } from 'models/User/User';
import moment from 'moment';

interface PickerValuesProps {
    metaInfoService?: MetaInfoService;
    sprinklrAPIService?: SprinklrAPIService;
    engine: AnalyticsEngine;
    report: string;
    dimension: WidgetDimensionKey;
    column?: any;
    autoBlur?: boolean;
    additional?: any; // Additional block for looking up values
    selectedValues?: Array<string>;
    filters?: Array<ReportingLookupFilter>;
    columnTypeFilter?: ColumnTypeFilter;
    onValuesLoaded?: (values: BulkLookupValue[]) => void;
    isBrandCompetitor?: boolean; // Special-case for BRAND_ID as data sources
    placeholder?: string;

    onChange?(
        selectedValues: Array<string>,
        columnType?: ChannelType,
        removedValues?: Array<string>
    ): void;

    valueLabelFunction?: (
        engine: AnalyticsEngine,
        report: string,
        dimension: WidgetDimensionKey,
        additional?: any,
        filters?: Array<ReportingLookupFilter>,
        query?: string,
        limit?: number,
        page?: number
    ) => Promise<BulkLookupValue[]>;
}

class PickerValues extends Picker<PickerValuesProps, any> {
    @observable
    private isLoading = false;

    @observable
    private initialOptions: any = [];

    @observable
    private selectedValues: string[] = [];

    @observable
    private filterOutValues: string[] = [];

    private lastQuery;
    private lastValues: DimensionValues;

    componentDidMount() {
        this.loadInitialOptions();
    }

    componentDidUpdate(prevProps: Readonly<PickerValuesProps>): void {
        if (this.props.selectedValues !== prevProps.selectedValues) {
            runInAction(() => {
                this.selectedValues = this.props.selectedValues;
            });
        }
    }

    @computed get isAnalytics(): boolean {
        return this.props.dimension.name !== 'dashboard' && this.props.dimension.name !== 'column';
    }

    @computed get isMulti(): boolean {
        return (
            this.isAnalytics &&
            this.props.dimension.name !== 'WOEID' &&
            this.props.dimension.name !== 'HIERARCHY_ID'
        );
    }

    render() {
        const {
            engine,
            report,
            additional,
            dimension,
            autoBlur,
            metaInfoService,
            placeholder,
        } = this.props;

        const status = this.getStatus(
            metaInfoService,
            engine,
            additional ? additional.report : report,
            [],
            this.isLoading
        );

        let noResultsText = i18n.t('No results');
        if (dimension.isEmpty()) {
            noResultsText = 'Select filter dimension first';
        }
        return (
            <StyledAsyncSelect
                key={this.initialOptions.length}
                multi={this.isMulti}
                isLoading={this.isLoading}
                loadOnFocus={false}
                loadOnMount={true}
                clearable={this.isMulti}
                closeOnSelect={!this.isMulti}
                noResultsMsg={noResultsText}
                searchingText={i18n.t('Searching...')}
                placeholder={placeholder ?? status.message}
                minimumInput={additional?.report ? 3 : 0}
                paginationBuffer={paginationCount}
                debounceInterval={100}
                initialOptions={toJS(this.initialOptions)}
                loadOptions={this.loadOptionsAsync.bind(this)}
                filterOptions={this.filterOptions.bind(this)}
                onChange={({ value }) => {
                    this.onChange(value);
                }}
                value={this.asyncFilterValues}
                resolveSelectedOptions={this.resolveSelectedOptions.bind(this)}
                getOptionLabel={this.getLabel.bind(this)}
                getValueLabel={this.getLabel.bind(this)}
                disabled={status.disabled}
            />
        );
    }

    @action
    private loadInitialOptions() {
        if (this.props.dimension?.isEmpty()) {
            return;
        }

        // LEGZ observations - mutating selectedValues as side effect of this loader isn't ideal
        // P.S. what keeps it in sync if props change? we seem to just rely on the component always being remounted - PERF ISSUE
        this.selectedValues = this.props.selectedValues;

        this.loadOptionsAsync(null, 0).then(result => {
            runInAction(() => {
                this.initialOptions = result.options || [];
            });
        });
    }

    @action
    private onChange = (selected: readonly Option[] | Option): void => {
        if (!this.props.onChange) {
            return;
        }
        if (!selected) {
            selected = [];
        }

        if (selected instanceof Array) {
            const selectedValues = this.selectedValues || [];
            const newValues = selected.map((item: BulkLookupValue) => item.id);

            let removed: string[];
            if (selected.length < selectedValues.length) {
                removed = _difference(selectedValues, newValues);
            }

            this.props.onChange(newValues, selected[0]?.channel, removed);
        } else {
            this.props.onChange([selected.id as string], selected.channel);
        }
    };

    @computed
    get asyncFilterValues() {
        return (
            this.selectedValues?.map(value => {
                return {
                    id: value,
                    // LEGZ - we no longer map the ID as a label here - this would causing a flicker of the full ID to be displayed in the selected option
                };
            }) || []
        );
    }

    @action
    private filterOptions(values: AsyncListItem[]) {
        const { columnTypeFilter } = this.props;

        if (columnTypeFilter?.length) {
            values = values.filter(value => value.type === columnTypeFilter);
        }

        if (this.filterOutValues.length) {
            values = values.filter(value => this.filterOutValues.indexOf(value.id) === -1);
        }

        // remove posts that are part of multi-photo posts with no text
        values = this.removePhotoOnlyPosts(values);
        return values;
    }

    @action
    private async resolveLabels(values, filters: any[], resolved: any[]): Promise<any> {
        const { engine, report, dimension, additional } = this.props;
        const isDashboard = engine === 'MONITORING_DASHBOARD';
        let unresolved = true;
        let hasMore = true;
        let page = 0;
        let resolvedDashboard;
        let firstRequest = true;

        while (hasMore && unresolved) {
            const data = await this.props.metaInfoService.getValueLabels(
                engine,
                report,
                dimension,
                additional,
                filters,
                '',
                4000,
                page,
                null
            );

            const valueFilter = (item: any) => {
                if (isDashboard) {
                    const found = item.columns?.find(item2 => values.indexOf(item2.id) !== -1);
                    if (found) {
                        resolvedDashboard = {
                            id: found.id,
                            label: item.name,
                        };
                    }
                }
                // Don't add values if they already exist.
                if (
                    resolved.some(
                        val =>
                            (item.postId?.toString() || item.id) ===
                            (val.postId?.toString() || val.id)
                    )
                ) {
                    return false;
                }
                return values.indexOf(item.postId?.toString() || item.id) !== -1;
            };

            this.filterOutValues.length = 0;
            const foundValues = data.values.filter(valueFilter);
            resolved.push.apply(resolved, foundValues);
            hasMore = data.hasMore;
            page++;
            unresolved = resolved.length != values.length;

            // Make an additional request for selected values, if not all captured in first request
            if (unresolved && firstRequest) {
                firstRequest = false;
                const data2 = await this.props.metaInfoService.getValueLabels(
                    engine,
                    report,
                    dimension,
                    additional,
                    filters,
                    '',
                    4000,
                    page,
                    values
                );

                const foundValues2 = data2.values.filter(valueFilter);
                resolved.push.apply(resolved, foundValues2);
            }

            unresolved = resolved.length != values.length;
        }

        return resolvedDashboard;
    }

    @action
    private async resolveLabelsFast(values, resolved: any[]): Promise<void> {
        const { engine, report, dimension, additional, isBrandCompetitor } = this.props;

        const results: BulkLookupValues = await this.props.sprinklrAPIService.bulkLookup(
            engine,
            report,
            dimension,
            values,
            null,
            null
        );

        if (results) {
            this.filterOutValues.length = 0;

            const keys = Object.keys(results);
            if (keys.length) {
                // Ensure ids are correct
                keys.forEach(key => {
                    // https://sentry.io/organizations/sprinklr-4d/issues/3357798756/?project=5740128&query=is%3Aunresolved&referrer=issue-stream&sort=user&statsPeriod=14d
                    if (results?.[key]?.hasOwnProperty('id')) {
                        results[key].id = key;
                    }
                });

                const foundValues = keys
                    .filter((key: any) => {
                        let found =
                            values.indexOf(results[key].postId?.toString() || results[key].id) !==
                            -1;
                        if (found && isBrandCompetitor !== undefined) {
                            found =
                                isBrandCompetitor === results[key].isCompetitor ||
                                isBrandCompetitor === results[key].competitorBrand;
                        }

                        return found;
                    })
                    .map(key => results[key]);

                resolved.push.apply(resolved, foundValues);
            }
        }
    }

    @action
    private async resolveSelectedOptions(values): Promise<any[]> {
        const { engine, dimension, isBrandCompetitor } = this.props;
        let { filters } = this.props;
        const isDashboard = engine === 'MONITORING_DASHBOARD';
        let resolvedDashboard;
        const useFastMethod =
            !isDashboard &&
            !filters &&
            (isBrandCompetitor !== undefined || dimension.name === 'FROM_SN_USER');
        const resolved: any[] = [];

        this.isLoading = true;

        // Narrowing scope to post ids for the moment, just to be on safe side,
        // Likely could be useful for other dimension types as well.
        if (dimension.name === 'POST_ID' && (!filters || !filters.length)) {
            filters = [
                {
                    field: dimension.name,
                    filterType: 'IN',
                    values,
                },
            ];
        }

        // Special-case for BRAND_ID in BENCHMARKING.  Other approach too slow.
        // TODO:  Could be expanded for all cases where "filters" is null,
        // but made it narrow to be safer.
        if (useFastMethod) {
            resolvedDashboard = await this.resolveLabelsFast(values, resolved);
        } else {
            resolvedDashboard = await this.resolveLabels(values, filters, resolved);
        }

        // Any values that couldn't be resolved are removed
        if (!isDashboard && values.length != resolved.length) {
            let newValues = this.selectedValues.slice();

            values.forEach(value => {
                if (
                    !resolved.find(item => item.id === value) &&
                    !resolved.find(item => item.postId?.toString() === value)
                ) {
                    // Create list excluding this value we couldn't find
                    newValues = newValues.filter(value2 => value2 !== value);
                }
            });

            // Special-case for BRAND_ID used as a data source for benchmarking
            // Same values are provided for both Brand and Competitors pickers.
            // Each value only is valid is one or the other of the pickers.
            if (useFastMethod) {
                // Remove this from the received values
                this.selectedValues = this.selectedValues.filter(
                    value => newValues.indexOf(value) !== -1
                );
            } else {
                this.onChange(
                    newValues.map(value => {
                        return { id: value };
                    })
                );
            }
        }

        const result = resolved.map((item: any) => {
            const message =
                item.contactInfo?.fullName ||
                item.processedContent?.message ||
                item.content?.message ||
                item.displayName ||
                item.name;
            return {
                id: item.postId || item.id,
                label: message,
                profileImgUrl: item.profileImgUrl,
            };
        });

        // Add any column matches to the resolved result
        if (resolvedDashboard) {
            result.push(resolvedDashboard);
        }

        this.isLoading = false;

        return result;
    }

    @action
    private getLabel(data) {
        const option = data.option;
        const social = option.snType?.toLowerCase();
        const profileImgUrl = option.profileImgUrl ?? DEFAULT_AVATAR_URL;
        const filterName = this.props.dimension?.name;

        //if label is 13 digit unix timestamp, format the label to be a readable date (Related to "Creation Date" filter for Custom Entity data source) - AMR
        let label = option?.label;
        const unixRegex = /^\d{13}/g;
        if (label?.match(unixRegex)) {
            const unixTime = moment.unix(label / 1000);
            label = moment(unixTime).format('ddd, Do MMM, YYYY');
        }

        const showAvatar = filterName === 'ACCOUNT_ID' || filterName === 'FROM_SN_USER';
        const showChannel =
            filterName === 'ACCOUNT_ID' ||
            filterName === 'FROM_SN_USER' ||
            filterName === 'SN_TYPE' ||
            filterName === 'LISTENING_MEDIA_TYPE';

        if (option.channelType && option.label === option.id) {
            return <span>{option.message}</span>;
        } else {
            return (
                <Flexbox middle gap='m'>
                    {showAvatar && (
                        <Avatar
                            key={option.id}
                            size='s'
                            user={{
                                url: profileImgUrl,
                            }}
                        />
                    )}
                    {showChannel && !showAvatar && <Channel channel={social} size='l' />}
                    <span>{label}</span>
                    {showChannel && showAvatar && <Channel channel={social} size='s' />}
                </Flexbox>
            );
        }
    }

    @action
    private async loadOptionsAsync(inputText: string, pageNumber: number) {
        // If there are no results found without a filter applied,
        // and you apply a filter, api will hang for a couple of minutes
        // This prevents that from happening
        if (this.isLoading || (this.lastQuery === inputText && this.lastValues?.noResults())) {
            return Promise.resolve({ complete: true, options: [] });
        }

        if (inputText !== this.lastQuery) {
            this.lastValues = null;
        }

        this.isLoading = true;
        this.lastQuery = inputText;

        const data = await this.props.metaInfoService.getValueLabels(
            this.props.engine,
            this.props.report,
            this.props.dimension,
            this.props.additional,
            this.props.filters,
            inputText,
            paginationCount,
            pageNumber,
            null,
            this.props.column
        );
        this.isLoading = false;

        this.lastValues = new DimensionValues(
            pageNumber,
            paginationCount,
            data.hasMore,
            data.values,
            this.lastValues,
            this.props.dimension.name === 'HIERARCHY_ID'
        );

        if (this.props.onValuesLoaded) {
            this.props.onValuesLoaded(this.lastValues.getValues().slice());
        }

        const result = this.lastValues.toAsyncList();
        return result;
    }

    private removePhotoOnlyPosts(values: Array<AsyncListItem>): AsyncListItem[] {
        return values.filter(value => {
            if (value.additional && !_isEmpty(value.additional)) {
                return (
                    !value.additional?.PART_OF_MULTI_PHOTO &&
                    (value.message ||
                        ['PAID_INITIATIVE', 'AD_SET'].includes(value.entityType) ||
                        value.additional.BRIEF_TEMPLATE_IDS)
                );
            } else {
                return value;
            }
        });
    }
}

export default inject('metaInfoService', 'sprinklrAPIService')(observer(PickerValues));
